import whenKey from 'ally.js/src/when/key';
import {
    NAV_ITEM_ACTIVE,
    MENU_EXPANDER_SELECTOR,
} from '../../primary-nav/js/global';

export default function _megaMenu() {
    /**
     * Mega menu selector
     * @const {string}
     */
    const MEGA_MENU_SELECTOR = '[data-component="mega-menu"]';

    /**
     * Mega menu list selector
     * @const {string}
     */
    const MEGA_MENU_LIST_SELECTOR = '.mega-menu__nav-list';

    /**
     * Mega menu item selector
     * @const {string}
     */
    const MEGA_MENU_ITEM_SELECTOR = '.mega-menu__nav-item';

    /**
     * Mega menu item has child class
     * @const {string}
     */
    const MEGA_MENU_ITEM_HAS_CHILD = 'mega-menu__nav-item--has-children';

    /**
     * Mega menu double teir element selector
     * @const {string}
     */
    const MEGA_MENU_DOUBLE_TEIR_SELECTOR = '.mega-menu-wrapper--double-teir';

    /**
     * Mega menu double teir element active class
     * @const {string}
     */
    const MEGA_MENU_DOUBLE_TEIR_ACTIVE = 'mega-menu-wrapper--double-active';

    /**
     * Mega menu double teir element selector
     * @const {string}
     */
    const MEGA_MENU_TEIR_1_LEVEL_SELECTOR = '.mega-menu__nav-list--level-1';

    /**
     * Mega menu double teir expantion selector
     * @const {string}
     */
    const MEGA_MENU_DOUBLE_TEIR_EXPAND_SELECTOR = '.mega-menu__nav-expand';

    /**
     * Mega menu double teir item active
     * @const {string}
     */
    const MEGA_MENU_DOUBLE_TEIR_ITEM_ACTIVE = 'mega-menu__nav-item--active';

    /**
     * Create a new Mega Menu
     * @param {HTMLElement} MegaMenu - The HTML element of the mega menu
     * @class
     */
    class MegaMenu {
        constructor(megaMenu) {
            this.megaMenu = megaMenu;

            this.allyHandles = {
                keyHandle: undefined,
            };

            this.allyHandles.keyHandle = whenKey({
                escape: () => {
                    setTimeout(this.closeMenu());
                },
            });

            // If the menu is double teir, attach expantion handlers
            const doubleTeir = megaMenu.matches(MEGA_MENU_DOUBLE_TEIR_SELECTOR);
            if (doubleTeir) {
                const menuExpanders = megaMenu.querySelectorAll(
                    MEGA_MENU_DOUBLE_TEIR_EXPAND_SELECTOR
                );
                menuExpanders.forEach((expander) => {
                    const navItem = expander.closest(MEGA_MENU_ITEM_SELECTOR);
                    expander.addEventListener('click', () => {
                        this._doubleTeirExpandMenu(navItem, true);
                    });

                    navItem.addEventListener('mouseover', () => {
                        this._doubleTeirExpandMenu(navItem);
                    });

                    navItem.addEventListener('mouseleave', () => {
                        this._doubleTeirRemoveHoverActive(navItem);
                    });
                });
            }
        }

        closeMenu() {
            // Is the current mega menu open
            const openMegaMenu = this.megaMenu.closest(`.${NAV_ITEM_ACTIVE}`);

            if (openMegaMenu) {
                // Does the currently open mega menu have focus within
                const currentFocus = document.activeElement;
                const isMenuInFocus = this.megaMenu.contains(currentFocus);

                // Close the menu
                openMegaMenu.classList.remove(NAV_ITEM_ACTIVE);

                if (isMenuInFocus) {
                    // Reset the focus to the menu expander button
                    openMegaMenu.querySelector(MENU_EXPANDER_SELECTOR).focus();
                }
            }
        }

        /**
         * Function to open the third navigation their when a second teir item is hovered or actioned. Dynamically adjusts the height of the
         * parent double teir container to fit the full body of the third menu tier if it is larger than the default
         *
         * @param {Element} navigationItem - Navigation item that was hovered or the closed nav item to the button that was clicked
         * @param {boolean} clickedOpen - If this function is being called from a button slick to open (true) or a hover (false)
         */
        _doubleTeirExpandMenu(navigationItem, clickedOpen) {
            const navItem = navigationItem;

            // Remove active from any siblings
            const allItems = navigationItem
                .closest(MEGA_MENU_TEIR_1_LEVEL_SELECTOR)
                .querySelectorAll(MEGA_MENU_ITEM_SELECTOR);
            allItems.forEach((item) => {
                item.classList.remove(MEGA_MENU_DOUBLE_TEIR_ITEM_ACTIVE);
            });

            const subNavHeight = navItem.querySelector(
                MEGA_MENU_LIST_SELECTOR
            ).clientHeight;
            const doubleTeir = navItem.closest(MEGA_MENU_DOUBLE_TEIR_SELECTOR);

            doubleTeir.style.setProperty(
                '--subNavHeightNeeded',
                `${subNavHeight}px`
            );

            if (navItem.classList.contains(MEGA_MENU_ITEM_HAS_CHILD)) {
                doubleTeir.classList.add(MEGA_MENU_DOUBLE_TEIR_ACTIVE);
            }

            if (clickedOpen) {
                // Add active class
                navItem.classList.add(MEGA_MENU_DOUBLE_TEIR_ITEM_ACTIVE);
            }
        }

        /**
         * Function handles removing active class added on hover when hover is lost
         *
         * @param {Element} navItem - Navigation item that has lost hover
         */
        _doubleTeirRemoveHoverActive(navItem) {
            const doubleTeir = navItem.closest(MEGA_MENU_DOUBLE_TEIR_SELECTOR);
            doubleTeir.classList.remove(MEGA_MENU_DOUBLE_TEIR_ACTIVE);
        }
    }

    // Finall all mega menus and created handler objects
    const megaMenus = document.querySelectorAll(MEGA_MENU_SELECTOR);
    megaMenus.forEach((megaMenu) => {
        // Create a new item to modify
        const DOMItem = megaMenu;

        // Attach the megamenu instance to our DOM Item
        if (!DOMItem.megaMenu) {
            DOMItem.megaMenu = new MegaMenu(megaMenu);
        }
    });
}

_megaMenu();
